// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component, OnInit, Input, Output, EventEmitter, ElementRef, ViewChild, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';

@Component({
    selector: 'app-numeric-input',
    templateUrl: './numeric-input.component.html',
    styleUrls: ['./numeric-input.component.scss'],
    standalone: false
})
export class NumericInputComponent implements OnInit, AfterViewInit, OnChanges {
  @Input()min: number;
  @Input()max: number;
  @Input()step: number;
  @Input()value: number;
  @Input()label: string;
  @Input()labelPosition: 'left' | 'right';
  @Input()inputPosition: 'top' | 'right' | 'bottom' | 'left';
  @Input()showMinMax: boolean;
  @Input()showProgressBar: boolean;
  @Input()disabled: boolean;
  @Input()inputStyles: {} = {};
  @Input()inputLabelStyles: {} = {};
  @Input()minLabelStyles: {} = {};
  @Input()maxLabelStyles: {} = {};
  @Output()valueChanged: EventEmitter<number> = new EventEmitter<number>();

  @ViewChild('inputValue') inputValue: ElementRef<HTMLInputElement>;
  
  intervalIncHandle: number;
  intervalDecHandle: number;
  progressValue: number;
  canvas: HTMLCanvasElement;
  // NOTE: If the values for any of the variables for fontSize, padding, margin, or border below change,
  // then the corresponding sass variables have to be updated to match.
  inputFontSize = '15px';
  inputPaddingLeft = 8;
  inputPaddingRight = 8;
  inputBorderLeft = 1;
  inputBorderRight = 1;
  inputMarginLeft = 0;
  inputMarginRight = 0;
  inputWidthMinimum = 40;

  constructor() {
    this.value = 1;
    this.step = 1;
    this.min = 1;
    this.max = 100;
    this.label = '';
    this.labelPosition = 'left';
    this.inputPosition = 'top';
    this.showMinMax = true;
    this.showProgressBar = true;
    this.progressValue = this.value;
    this.disabled = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Check currentValue for undefined
    if (changes.value && changes.value.currentValue !== undefined) {
      // this.min -1 is to force an update in the onValueChanges 
      // in case the previousValue is undefined.
      this.value = changes.value.previousValue || this.min - 1;
      this.onValueChanged(null, changes.value.currentValue);
    }
  }

  ngOnInit() {
    this.progressValue = this.calculateProgressValue(+this.value);
  }

  ngAfterViewInit(): void {
    if (this.inputValue && this.inputValue.nativeElement) {
      this.inputValue.nativeElement.style.width = this.calculateTextWidth(this.value, 'normal', '15px', 'Arial');
    }
  }

  incrementValue() {
    let value: number = +this.value;
    const max: number =  +this.max;
    const step: number = +this.step;
    value = value < max ? value + step : max;
    this.onValueChanged(null, value);
  }

  decrementValue() {
    let value: number = +this.value;
    const min: number = +this.min;
    const step: number = +this.step;
    value = value > min ? value - step : min;
    this.onValueChanged(null, value);
  }

  mousedownIncrement() {
    const value: number = +this.value;
    const min: number = +this.min;
    const max: number =  +this.max;
    if (value >= min && value < max) {
      let firstPass = true;
      this.intervalIncHandle = window.setInterval(() => {
        // Skip the first pass so it does not generate 2 increments from click event and this one.
        if (!firstPass) {
          this.incrementValue();
        }
        firstPass = false;
      }, 125);
    }
  }

  mouseupIncrement() {
    if (this.intervalIncHandle) {
      clearInterval(this.intervalIncHandle);
      this.intervalIncHandle = null;
    }
  }

  mousedownDecrement() {
    const value: number = +this.value;
    const min: number = +this.min;
    const max: number =  +this.max;
    if (value > min && value <= max) {
      let firstPass = true;
      this.intervalDecHandle = window.setInterval(() => {
          // Skip the first pass so it does not generate 2 decrements from click event and this one.
          if (!firstPass) {
            this.decrementValue();
          }
          firstPass = false;
      }, 125);
    }
  }

  mouseupDecrement() {
    if (this.intervalDecHandle) {
      clearInterval(this.intervalDecHandle);
      this.intervalDecHandle = null;
    }
  }

  onValueChanged(event, value: number | null) {
    if (!event) {
      value = Math.ceil(+value);
    } else {
      value = Math.ceil(event.target.value);
    }

    const min: number = +this.min;
    const max: number =  +this.max;
    if (value > max) { value = max; }
    // Allow user to type in value less than min, but not onBlur.
    // If min is 5 and they want to type 12 they have to type a 1 first.
    if (event && event.type === 'blur') {
      if (value < min) { value = min; }
    }
    if (+this.value !== value) {
      this.value = value;
      this.progressValue = this.calculateProgressValue(value);

      if (this.inputValue && this.inputValue.nativeElement) {
        this.inputValue.nativeElement.style.width = this.calculateTextWidth(this.value, 'normal', this.inputFontSize, 'Arial');
      }

      // We may allow them to type in a number less than min, but we won't report it to the parent.
      // OnBlur will set it to min and report the min value.
      if (value >= min) {
        this.valueChanged.emit(value);
      }
      
    }
    if (event) {
      event.target.value = value;
    }
    
  }

  calculateProgressValue(value: number): number {
    return Math.round(value * (100 / +this.max));
  }

  setInputPosition() {
    return `input-position-${this.inputPosition}`;
  }

  calculateTextWidth(text, fontWeight, fontSize, fontFamily): string {
    let calculatedWidth: string;
    
    try {
        // re-use canvas object for better performance
      this.canvas = this.canvas || document.createElement('canvas');
      const context = this.canvas.getContext('2d');
      // context.font seems to always convert to px regardless of what you pass in as fontSize. 
      // 2.5em, xx-large, inherit, 10pt, etc. are always converted to px
      // so we can just append px to our calculated value.
      context.font = `${fontWeight} ${fontSize} ${fontFamily}`;
      const metrics = context.measureText(text);
      const width = metrics.width;
      let calcWidth = Math.ceil(
          this.inputPaddingLeft +
          this.inputPaddingRight +
          this.inputBorderLeft +
          this.inputBorderRight +
          this.inputMarginLeft +
          this.inputMarginRight +
          width
      );
      calcWidth = calcWidth >= this.inputWidthMinimum ? calcWidth : this.inputWidthMinimum;
      calculatedWidth = `${calcWidth}px`;
    } catch {
      // Something sensible in case of disaster.
      calculatedWidth = `${this.inputWidthMinimum}px`;
    }

    return calculatedWidth;
  }
  
}
